/** 将胸卡渲染为彩色 PNG（fetch 拉图，避免 crossOrigin 导致打印发灰） */

import QRCode from 'qrcode'
import { BASE_URL } from '@/api'
import { DEFAULT_IDENTITY_SLOT, DEFAULT_NAME_SLOT, normalizeSlot } from '@/utils/badgeTextSlotStyle.js'

const RENDER_SCALE = 2

function layoutSize(layoutType) {
  const u = String(layoutType || '').toUpperCase()
  if (u === 'PORTRAIT') return { w: 600, h: 950 }
  if (u === 'SQUARE') return { w: 800, h: 800 }
  return { w: 950, h: 600 }
}

function resolveImageUrl(url) {
  const u = String(url || '').trim()
  if (!u) return ''
  if (/^https?:\/\//i.test(u) || u.startsWith('data:')) return u
  return `${BASE_URL}${u.startsWith('/') ? '' : '/'}${u}`
}

/** 通过 fetch 取 Blob 再绘图，同源代理下可保留完整彩色 */
async function loadImageColor(src) {
  const url = String(src || '').trim()
  if (!url) throw new Error('empty')
  if (url.startsWith('data:')) {
    return loadImageElement(url)
  }
  const res = await fetch(url, { credentials: 'same-origin', mode: 'cors' })
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`)
  }
  const blob = await res.blob()
  const obj = URL.createObjectURL(blob)
  try {
    return await loadImageElement(obj)
  } finally {
    URL.revokeObjectURL(obj)
  }
}

function loadImageElement(src) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error('图片解码失败'))
    img.src = src
  })
}

function drawText(ctx, text, slot, w, h, defaults) {
  const s = normalizeSlot(slot, defaults)
  const x = (s.xPct / 100) * w
  const y = (s.yPct / 100) * h
  const fs = s.fontSizePx
  ctx.font = `${s.fontWeight} ${fs}px "Microsoft YaHei", "PingFang SC", sans-serif`
  ctx.fillStyle = s.color
  ctx.textAlign = s.textAlign
  ctx.textBaseline = 'middle'
  const lines = String(text || '').split('\n')
  const lineH = fs * s.lineHeight
  const startY = y - ((lines.length - 1) * lineH) / 2
  lines.forEach((line, i) => {
    ctx.fillText(line, x, startY + i * lineH)
  })
}

async function renderSide(card, w, h, mode) {
  const canvas = document.createElement('canvas')
  canvas.width = w * RENDER_SCALE
  canvas.height = h * RENDER_SCALE
  const ctx = canvas.getContext('2d', { alpha: false })
  if (!ctx) throw new Error('Canvas 不可用')
  ctx.scale(RENDER_SCALE, RENDER_SCALE)
  const bgUrl = resolveImageUrl(card.imageUrl)
  let drewBg = false
  if (bgUrl) {
    try {
      const img = await loadImageColor(bgUrl)
      ctx.drawImage(img, 0, 0, w, h)
      drewBg = true
    } catch (e) {
      console.warn('[badgeRender] bg load failed, retry element', e)
      try {
        const img = await loadImageElement(bgUrl)
        ctx.drawImage(img, 0, 0, w, h)
        drewBg = true
      } catch {
        /* fall through */
      }
    }
  }
  if (!drewBg) {
    ctx.fillStyle = '#1a1a2e'
    ctx.fillRect(0, 0, w, h)
  }
  if (mode === 'front') {
    drawText(ctx, card.displayName, card.nameSlot, w, h, DEFAULT_NAME_SLOT)
    drawText(ctx, card.identityTitle, card.identitySlot, w, h, DEFAULT_IDENTITY_SLOT)
  } else {
    const shareUrl = String(card.shareQrUrl || '').trim()
    if (shareUrl) {
      const qrDataUrl = await QRCode.toDataURL(shareUrl, {
        margin: 2,
        width: Math.min(400, Math.round(w * 0.36)),
        color: { dark: '#000000', light: '#ffffff' },
      })
      const qrImg = await loadImageElement(qrDataUrl)
      const qw = Math.min(w * 0.38, h * 0.5)
      const qx = (w - qw) / 2
      const qy = (h - qw) / 2
      ctx.fillStyle = 'rgba(255,255,255,0.94)'
      const pad = 14
      ctx.fillRect(qx - pad, qy - pad, qw + pad * 2, qw + pad * 2)
      ctx.drawImage(qrImg, qx, qy, qw, qw)
      ctx.font = '600 22px "Microsoft YaHei", sans-serif'
      ctx.fillStyle = '#ffffff'
      ctx.strokeStyle = 'rgba(0,0,0,0.35)'
      ctx.lineWidth = 3
      ctx.textAlign = 'center'
      const hint = '扫码查看胸卡'
      ctx.strokeText(hint, w / 2, qy + qw + pad + 24)
      ctx.fillText(hint, w / 2, qy + qw + pad + 24)
    } else {
      ctx.font = '500 24px "Microsoft YaHei", sans-serif'
      ctx.fillStyle = '#ffffff'
      ctx.textAlign = 'center'
      ctx.fillText('（预览无扫码链接）', w / 2, h / 2)
    }
  }
  return canvas.toDataURL('image/png')
}

/**
 * @param {object} card
 * @returns {Promise<{ front: string, back: string }>}
 */
export async function renderBadgePrintImages(card) {
  if (!card || !card.available) {
    throw new Error('胸卡数据不可用')
  }
  const { w, h } = layoutSize(card.layoutType)
  const front = await renderSide(card, w, h, 'front')
  const back = await renderSide(card, w, h, 'back')
  return { front, back }
}

function triggerDownload(dataUrl, filename) {
  const a = document.createElement('a')
  a.href = dataUrl
  a.download = filename
  a.rel = 'noopener'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
}

/** 上下拼接正反面为一张图，便于一次打开查看 */
async function combineFrontBackVertical(frontDataUrl, backDataUrl) {
  const front = await loadImageElement(frontDataUrl)
  const back = await loadImageElement(backDataUrl)
  const gap = 32 * RENDER_SCALE
  const labelH = 36 * RENDER_SCALE
  const w = Math.max(front.width, back.width)
  const h = front.height + back.height + gap + labelH * 2
  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d', { alpha: false })
  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, w, h)
  const fx = (w - front.width) / 2
  ctx.font = `600 ${14 * RENDER_SCALE}px "Microsoft YaHei", sans-serif`
  ctx.fillStyle = '#667085'
  ctx.textAlign = 'left'
  ctx.fillText('正面（姓名 / 职务）', fx, labelH - 8 * RENDER_SCALE)
  ctx.drawImage(front, fx, labelH)
  const by = labelH + front.height + gap
  ctx.fillText('反面（扫码查看胸卡）', fx, by + labelH - 8 * RENDER_SCALE)
  ctx.drawImage(back, (w - back.width) / 2, by + labelH)
  return canvas.toDataURL('image/png')
}

/**
 * 下载一张彩色 PNG（上正面、下反面；打印时仍可用浏览器「打印」输出 2 页）
 */
export async function downloadBadgeColorPng(card) {
  const { front, back } = await renderBadgePrintImages(card)
  const combined = await combineFrontBackVertical(front, back)
  const base = String(card.displayName || '胸卡').replace(/[/\\?%*:|"<>]/g, '_')
  triggerDownload(combined, `${base}-胸卡正反面.png`)
}
